// src/components/warranties/WarrantyPhotoUploader.jsx
import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, LinearProgress, Stack, Typography, Alert, Chip } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../utils/firebase/firebase";
import { useTheme } from "@mui/material/styles";

function uid() {
    // simples e suficiente p/ nomes únicos
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export default function WarrantyPhotoUploader({
    warrantyId,
    disabled,
    onUploadedUrls, // (urlsNovas: string[]) => void
    existingUrlsCount = 0,
}) {
    const [state, setState] = useState({
        status: "idle", // idle | uploading | error
        message: "",
        progress: 0,
    });
    const theme = useTheme();


    const canUse = !!warrantyId && !disabled;

    const accept = useMemo(
        () => ({
            "image/*": [".png", ".jpg", ".jpeg", ".webp", ".heic"],
        }),
        []
    );

    const uploadOne = async (file) => {
        const safeName = String(file.name || "photo").replace(/[^\w.\-]+/g, "_");
        const path = `warranties/${warrantyId}/pictures/${uid()}_${safeName}`;

        const storageRef = ref(storage, path);
        const task = uploadBytesResumable(storageRef, file, {
            contentType: file.type || "image/jpeg",
            cacheControl: "public, max-age=31536000",
        });

        return new Promise((resolve, reject) => {
            task.on(
                "state_changed",
                (snap) => {
                    const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
                    setState((p) => ({ ...p, status: "uploading", progress: pct, message: "" }));
                },
                (err) => reject(err),
                async () => {
                    const url = await getDownloadURL(task.snapshot.ref);
                    resolve({
                        url,
                        path, // 👈 esse path você já tem
                    });
                }
            );
        });
    };

    const onDrop = useCallback(
        async (files) => {
            if (!canUse) return;
            if (!files?.length) return;

            try {
                setState({ status: "uploading", message: "", progress: 0 });

                // faz upload sequencial (mais previsível). Depois a gente paraleliza se quiser.
                const urls = [];
                for (const f of files) {
                    // limite de 10MB por arquivo (ajustável)
                    if (f.size > 10 * 1024 * 1024) {
                        throw new Error(`Arquivo muito grande: ${f.name} (máx 10MB)`);
                    }
                    const url = await uploadOne(f);
                    urls.push(url);
                }

                setState({ status: "idle", message: "", progress: 0 });
                if (typeof onUploadedUrls === "function") onUploadedUrls(urls);
            } catch (e) {
                setState({
                    status: "error",
                    message: e?.message || "Falha no upload",
                    progress: 0,
                });
            }
        },
        [canUse, warrantyId, onUploadedUrls]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        multiple: true,
        disabled: !canUse,
    });

    const dropSx = {
        borderRadius: 2,
        p: 2,
        cursor: canUse ? "pointer" : "not-allowed",
        opacity: canUse ? 1 : 0.6,
        userSelect: "none",
        transition: "all 0.15s ease",

        // fundo estilo "TextField"
        backgroundColor:
            theme?.palette?.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "rgba(2,6,23,0.04)",

        // borda estilo "TextField"
        border: "1px dashed",
        borderColor:
            theme?.palette?.mode === "dark"
                ? "rgba(255,255,255,0.18)"
                : "rgba(2,6,23,0.18)",

        "&:hover": {
            borderColor:
                theme?.palette?.mode === "dark"
                    ? "rgba(255,255,255,0.28)"
                    : "rgba(2,6,23,0.28)",
            backgroundColor:
                theme?.palette?.mode === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(2,6,23,0.06)",
        },

        // quando está arrastando arquivo por cima
        ...(isDragActive
            ? {
                borderStyle: "solid",
                borderWidth: 2,
                backgroundColor:
                    theme?.palette?.mode === "dark"
                        ? "rgba(255,255,255,0.10)"
                        : "rgba(2,6,23,0.08)",
            }
            : null),
    };


    if (!warrantyId) {
        return (
            <Alert severity="warning">
                Para anexar as fotos, primeiro salve a garantia (precisamos do ID para salvar em{" "}
                <b>warranties/{`{warrantyId}`}/pictures</b>).
            </Alert>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, gap: 1, flexWrap: "wrap" }}>
                <Typography fontWeight={900}>Fotos da garantia</Typography>
                <Chip size="small" variant="outlined" label={`Já anexadas: ${existingUrlsCount}`} />
            </Stack>

            {state.status === "error" && <Alert severity="error" sx={{ mb: 1 }}>{state.message}</Alert>}

            <Box
                {...getRootProps()}
                sx={dropSx}
            >
                <input {...getInputProps()} />
                <Typography fontWeight={800}>
                    {isDragActive ? "Solte as imagens aqui…" : "Arraste e solte imagens aqui (ou clique para selecionar)"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                    PNG/JPG/WEBP/HEIC • até 10MB por arquivo.
                </Typography>

                {state.status === "uploading" && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={state.progress}
                            sx={{
                                height: 8,
                                borderRadius: 999,

                                // fundo (trilho)
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.10)"
                                        : "rgba(2,6,23,0.08)",

                                // barra de progresso (verde sucesso)
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    backgroundColor: theme.palette.success.main,
                                    boxShadow:
                                        theme.palette.mode === "dark"
                                            ? "0 0 8px rgba(34,197,94,0.45)"
                                            : "0 0 6px rgba(34,197,94,0.35)",
                                },
                            }}
                        />

                        <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                            Enviando… {state.progress}%
                        </Typography>
                    </Box>
                )}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Button
                    variant="contained"
                    disabled={!canUse}
                    color="info"
                    onClick={() => {
                        // abre o file picker ao clicar na área (dropzone já faz isso),
                        // esse botão é só uma affordance extra; pode remover se quiser.
                    }}
                >
                    Selecionar imagens
                </Button>
            </Stack>
        </Box>
    );
}
